// Missed / delayed delivery: set the NEXT delivery date + time window of an
// order (store time, UAE). The shop (web + app) shows it on My Orders and the
// order page instead of the original estimate, and the customer gets a
// notification. Without a new date the shop shows "Delayed" once the original
// delivery time has passed.
//
// API (admin token): POST admin/order-delivery-date
//   { order_id, guest?, delivery_from, delivery_to?, note?, notify_customer? }
//   { order_id, guest?, clear: true }   -> back to the original estimate
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { adminPost } from './CategoryExtraProducts.tsx';

const STORE_TZ = 'Asia/Dubai';
// store wall-clock now as 'YYYY-MM-DDTHH:MM' (datetime-local format)
const storeNow = () => {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: STORE_TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date()).reduce((a: any, x) => { a[x.type] = x.value; return a; }, {});
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};
const toInput = (v?: string | null) => (v ? String(v).slice(0, 16).replace(' ', 'T') : '');

export type DeliveryDateOrder = {
  id: number;
  invoice_no?: string | null;
  estimated_delivery_text?: string | null;
  delivery_delayed?: boolean;
  delivery_rescheduled?: boolean;
  revised_delivery_from?: string | null;
  revised_delivery_to?: string | null;
  revised_delivery_note?: string | null;
};

// chips for the order list: "Delayed" (red) / "Rescheduled" (blue)
export function DeliveryStateChip({ order }: { order: any }) {
  if (order?.delivery_delayed) {
    return <Chip size="small" label="Delayed" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#fde8e8', color: '#c62828' }} />;
  }
  if (order?.delivery_rescheduled) {
    return <Chip size="small" label="Rescheduled" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#e8f0fe', color: '#1a56db' }} />;
  }
  return null;
}

export default function DeliveryDateDialog({
  order,
  guest = false,
  onClose,
  onSaved,
  onError,
}: {
  order: DeliveryDateOrder | null;
  guest?: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void; // errors as a popup (page snackbar)
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState<'' | 'save' | 'clear'>('');

  useEffect(() => {
    if (!order) return;
    setFrom(toInput(order.revised_delivery_from));
    setTo(toInput(order.revised_delivery_to));
    setNote(order.revised_delivery_note || '');
    setNotify(true);
    setBusy('');
  }, [order]);

  if (!order) return null;

  const save = async () => {
    if (!from) return onError('Choose the new delivery date and time.');
    if (to && to < from) return onError('"Delivery to" must be after "Delivery from".');
    if ((to || from) <= storeNow()) return onError('The new delivery time is already past (store time, UAE).');
    setBusy('save');
    try {
      const res = await adminPost('admin/order-delivery-date', {
        order_id: order.id,
        guest,
        delivery_from: from.replace('T', ' '),
        delivery_to: (to || from).replace('T', ' '),
        note: note.trim(),
        notify_customer: notify,
      });
      onSaved(res?.message || 'New delivery date saved');
    } catch (e: any) {
      onError(e?.message || 'Could not save the new delivery date.');
    } finally {
      setBusy('');
    }
  };

  const clear = async () => {
    if (!window.confirm('Remove the new delivery date? The customer sees the original estimate again (or "Delayed" if it has passed).')) return;
    setBusy('clear');
    try {
      const res = await adminPost('admin/order-delivery-date', { order_id: order.id, guest, clear: true });
      onSaved(res?.message || 'New delivery date removed');
    } catch (e: any) {
      onError(e?.message || 'Could not remove the new delivery date.');
    } finally {
      setBusy('');
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Delivery date · Order #{order.id}
        {order.invoice_no ? (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>{order.invoice_no}</Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#f6f6f7' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {order.delivery_rescheduled ? 'Current (new) delivery date' : 'Estimated delivery'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.estimated_delivery_text || '—'}</Typography>
            <DeliveryStateChip order={order} />
          </Box>
          {order.revised_delivery_note ? (
            <Typography variant="caption" color="text.secondary">Note: {order.revised_delivery_note}</Typography>
          ) : null}
        </Box>
        {order.delivery_delayed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The delivery time has passed. Customers see this order as <b>Delayed</b> until you set the next delivery date.
          </Alert>
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Next delivery (store time, UAE)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              type="datetime-local"
              label="Delivery from *"
              value={from}
              onChange={e => setFrom(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: storeNow() }}
              disabled={!!busy}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              type="datetime-local"
              label="Delivery to"
              value={to}
              onChange={e => setTo(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: from || storeNow() }}
              helperText="Optional - for a time window (e.g. 7 pm - 10 pm)"
              disabled={!!busy}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note for the customer (optional)"
              placeholder="e.g. Sorry, we missed you - we will come again"
              value={note}
              onChange={e => setNote(e.target.value.slice(0, 255))}
              fullWidth
              size="small"
              disabled={!!busy}
            />
          </Grid>
        </Grid>
        {!guest && (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={notify} onChange={e => setNotify(e.target.checked)} disabled={!!busy} />}
            label={<Typography variant="body2">Notify the customer (bell + phone notification)</Typography>}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {order.delivery_rescheduled && (
          <Button
            variant="contained"
            onClick={clear}
            disabled={!!busy}
            sx={{ mr: 'auto', textTransform: 'none', boxShadow: 'none', bgcolor: '#fdecec', color: '#c62828', '&:hover': { bgcolor: '#fbd5d5', boxShadow: 'none' } }}
          >
            {busy === 'clear' ? <CircularProgress size={18} /> : 'Remove new date'}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onClose}
          disabled={!!busy}
          sx={{ textTransform: 'none', boxShadow: 'none', bgcolor: '#f1f2f4', color: '#202223', '&:hover': { bgcolor: '#e4e5e7', boxShadow: 'none' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!!busy || !from}
          sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#008060', '&:hover': { bgcolor: '#006e52' } }}
        >
          {busy === 'save' ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save new date'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
